import type { LinkedPath, ResolveMap, ResolveMapEntry, Session } from '@ms-cloudpack/common-types';
import { bulletedList, cyan, type BulletList } from '@ms-cloudpack/task-reporter';
import {
  diffResolveMaps,
  findResolveMapEntry,
  type DiffResolveMapsChanges,
  type DiffResolveMapsStats,
} from '@ms-cloudpack/package-utilities';

export function formatLinkSummary(options: {
  session: Session;
  resolveMap: ResolveMap;
  linkedPaths: LinkedPath[];
  resolveStrategy?: 'dedupe' | 'duplicate';
}): string {
  const { session, linkedPaths, resolveMap, resolveStrategy } = options;
  const differences = diffResolveMaps({ before: session.resolveMap, after: resolveMap });

  const linkedPathsMessage = linkedPaths.length
    ? [
        'Linked paths:',
        bulletedList(
          linkedPaths
            .map((p) => p.path)
            .sort()
            .map((p) => cyan(p)),
        ),
      ]
    : ['No paths linked.'];

  const messages = [
    `Finished linking session: "${session.projectName}" (${session.id})`,
    '',
    ...linkedPathsMessage,
    '',
    `Resolve map differences:`,
    bulletedList(formatDiff(differences)),
    ...duplicatedMessages({ session, differences, resolveStrategy }),
    '',
  ];

  return messages.join('\n');
}

function formatDiff(diff: DiffResolveMapsStats): BulletList {
  const messages = [];
  const added = formatDiffChanges(diff.added);
  const removed = formatDiffChanges(diff.removed);

  if (!added.length && !removed.length) {
    messages.push('No differences found');
  }

  if (added.length) {
    messages.push('Added:', added);
  }

  if (removed.length) {
    messages.push('Removed:', removed);
  }

  return messages;
}

function formatDiffChanges(changes: DiffResolveMapsChanges): BulletList {
  const messages = [];
  if (changes.linked.length) {
    messages.push('Linked:', changes.linked.map(formatEntry).sort());
  }

  if (changes.internal.length) {
    messages.push('Internal:', changes.internal.map(formatEntry).sort());
  }

  if (changes.external.length) {
    messages.push('External:', changes.external.map(formatEntry).sort());
  }

  return messages;
}

function formatEntry(entry: ResolveMapEntry): string {
  return `${entry.name}@${entry.version} (${entry.path})`;
}

function duplicatedMessages(options: {
  session: Session;
  differences: DiffResolveMapsStats;
  resolveStrategy?: 'dedupe' | 'duplicate';
}): string[] {
  const { session, differences, resolveStrategy } = options;
  const duplicated = findDuplicates({ session, differences });

  return duplicated.length
    ? [
        '',
        `Warning:`,
        'The following packages were duplicated by linking:',
        duplicated.join(', '),
        '',
        'This could lead to unexpected behavior. Please review the linked paths and resolve map to ensure correctness.',
        ...(resolveStrategy == 'duplicate'
          ? ['Consider running link with `cloudpack link --resolve-strategy dedupe` to resolve these duplicates.']
          : []),
      ]
    : [];
}

/**
 * Find entries that were duplicated by linking.
 * This means that the package already existed in the resolve map,
 * and now there are more versions of the package than there were before.
 */
function findDuplicates(options: { session: Session; differences: DiffResolveMapsStats }): string[] {
  const { session, differences } = options;
  const duplicated = new Map<string, number>();

  for (const packageType of Object.values(differences.added)) {
    for (const change of packageType as ResolveMapEntry[]) {
      const { name } = change;
      const entry = findResolveMapEntry({ packageName: name, resolveMap: session.resolveMap });
      if (entry) {
        duplicated.set(name, (duplicated.get(name) || 0) + 1);
      }
    }
  }

  for (const packageType of Object.values(differences.removed)) {
    for (const change of packageType as ResolveMapEntry[]) {
      const { name } = change;
      if (duplicated.has(name)) {
        // We already checked for existence in the resolve map, so we know this will be a number.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        duplicated.set(name, duplicated.get(name)! - 1);
      }
    }
  }

  return Array.from(duplicated.entries())
    .filter(([, value]) => value > 0)
    .map(([key]) => key)
    .sort();
}
